import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, Package, Eye, ShoppingCart, Edit, Trash2, Plus, Store,
  AlertCircle, Upload, X, Camera, DollarSign, Tag, Check, Loader2,
  TrendingUp, ArrowRight, Sparkles,
  MessageSquare, Bell
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { colorMap } from '../modules/colorMap'; 
import { categories } from '../modules/categories';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useProducts } from '../contexts/ProductsContext';


function AddProductForm({ onClose, onSubmit, userInfo }) {
  const [formData, setFormData] = useState({
    name: '', 
    price: '', 
    category: '', 
    description: '', 
    isFixedPrice: true, // true = prix fixe, false = prix variables
    variablePrices: [{ price: '', quantity: '' }] // [{price: '', quantity: ''}]
  });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  // Ajouter un nouveau prix variable
  const addVariablePrice = () => {
    setFormData(prev => ({
      ...prev,
      variablePrices: [...prev.variablePrices, { price: '', quantity: '' }]
    }));
  };

  // Supprimer un prix variable
  const removeVariablePrice = (index) => {
    setFormData(prev => ({
      ...prev,
      variablePrices: prev.variablePrices.filter((_, i) => i !== index)
    }));
  };

  // Modifier un prix variable
  const handleVariablePriceChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      variablePrices: prev.variablePrices.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
    if (errors.variablePrices) setErrors(prev => ({ ...prev, variablePrices: '' }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    // Une seule image autorisée
    if (images.length > 0) {
      alert('Une seule image est autorisée. Supprimez l\'image actuelle pour en ajouter une nouvelle.');
      return;
    }

    setUploading(true);
    try {
      const file = files[0];
      if (file.size > 5 * 1024 * 1024) {
        throw new Error(`L'image ${file.name} est trop grande (max 5MB)`);
      }

      // Upload vers Supabase Storage
      const userId = userInfo?.id || 'anonymous';
      const fileName = `${userId}/${Date.now()}_${Math.random().toString(36).substring(2, 10)}.${file.name.split('.').pop()}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(fileName, file, { cacheControl: '3600', upsert: false });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('product-images')
        .getPublicUrl(uploadData.path);

      setImages([{
        id: uploadData.path,
        url: publicUrl,
        thumbnailUrl: publicUrl,
        fileId: uploadData.path,
        name: file.name
      }]);
    } catch (error) {
      console.error('Erreur upload:', error);
      alert(error.message || 'Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async (imagePath) => {
    try {
      await supabase.storage.from('product-images').remove([imagePath]);
      setImages([]);
    } catch (error) {
      console.error('Erreur suppression image:', error);
      setImages([]);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Nom requis';
    if (!formData.category) newErrors.category = 'Catégorie requise';
    if (images.length === 0) newErrors.images = 'Une photo est requise';
    
    // Validation selon le type de prix
    if (formData.isFixedPrice) {
      if (!formData.price || formData.price <= 0) {
        newErrors.price = 'Prix valide requis';
      }
    } else {
      // Validation des prix variables
      const validPrices = formData.variablePrices.filter(
        vp => vp.price && vp.quantity && vp.price > 0 && vp.quantity > 0
      );
      if (validPrices.length === 0) {
        newErrors.variablePrices = 'Au moins un prix avec quantité valide requis';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const imageData = images.map(img => ({
        url: img.url,
        thumbnailUrl: img.thumbnailUrl,
        fileId: img.fileId
      }));

      const productData = {
        name: formData.name,
        category: formData.category,
        description: formData.description || '',
        images: imageData,
        isFixedPrice: formData.isFixedPrice
      };

      // Ajouter le prix selon le type
      if (formData.isFixedPrice) {
        productData.price = parseFloat(formData.price);
      } else {
        productData.variablePrices = formData.variablePrices
          .filter(vp => vp.price && vp.quantity && vp.price > 0 && vp.quantity > 0)
          .map(vp => ({
            price: parseFloat(vp.price),
            quantity: parseInt(vp.quantity)
          }));
        productData.price = productData.variablePrices[0]?.price || 0;
      }

      // Insérer via Supabase
      const userId = userInfo?.id;
      if (!userId) throw new Error('Utilisateur non connecté ou ID manquant');
      const { data: product, error: insertError } = await supabase
        .from('products')
        .insert({
          user_id: userId,
          name: productData.name,
          price: productData.price,
          category: productData.category,
          description: productData.description,
          images: productData.images,
          tags: [],
          status: 'active',
        })
        .select()
        .single();

      if (insertError) throw insertError;
      
      // Déclencher les notifications push en arrière-plan (sans bloquer le succès du produit)
      supabase.functions.invoke('send-push', {
        body: {
          productName: productData.name,
          sellerName: userInfo?.name || 'Un vendeur'
        }
      }).catch(err => console.error('Push notification trigger error:', err));

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setIsSubmitting(false);
        onSubmit(product);
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.message || 'Erreur ajout produit');
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
        <div className="max-w-sm w-full mx-4 rounded-2xl p-10 text-center" style={{ backgroundColor: colorMap.bg.white }}>
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: colorMap.primary.light }}>
            <Check size={40} style={{ color: colorMap.primary.base }} />
          </div>
          <h3 className="text-2xl font-bold mb-3" style={{ color: colorMap.text.primary }}>Produit publié avec succès !</h3>
          <p className="text-base" style={{ color: colorMap.text.secondary }}>Votre annonce est maintenant visible par tous les utilisateurs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="min-h-screen px-4 py-8">
        <div className="max-w-2xl mx-auto rounded-2xl overflow-hidden" style={{ backgroundColor: colorMap.bg.white }}>
          <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${colorMap.border.default}` }}>
            <div>
              <h2 className="text-xl font-bold" style={{ color: colorMap.text.primary }}>Nouveau produit</h2>
              <p className="text-sm" style={{ color: colorMap.text.secondary }}>Remplissez les informations pour publier votre annonce</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" style={{ color: colorMap.text.dark }}>
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Photo unique */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Camera size={20} style={{ color: colorMap.secondary.base }} />
                <h3 className="text-base font-semibold" style={{ color: colorMap.text.primary }}>Photo du produit</h3>
                <span className="text-sm" style={{ color: colorMap.text.muted }}>(1 photo, max 5MB)</span>
              </div>

              <div className="flex gap-3">
                {images.length > 0 ? (
                  <div className="relative w-32 h-32 rounded-lg overflow-hidden group">
                    <img src={images[0].url} alt="Preview" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(images[0].id)}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', color: colorMap.bg.white }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <label className="w-32 h-32 rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer hover:border-orange-400 transition-colors">
                    {uploading ? (
                      <>
                        <Loader2 size={24} className="animate-spin" style={{ color: colorMap.secondary.base }} />
                        <span className="text-xs mt-2" style={{ color: colorMap.secondary.base }}>Upload...</span>
                      </>
                    ) : (
                      <>
                        <Upload size={24} style={{ color: colorMap.text.muted }} />
                        <span className="text-xs mt-2 text-center px-2" style={{ color: colorMap.text.muted }}>Ajouter une photo</span>
                      </>
                    )}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleImageUpload} 
                      className="hidden" 
                      disabled={uploading || images.length > 0} 
                    />
                  </label>
                )}
              </div>
              {errors.images && <p className="text-sm mt-2" style={{ color: '#ef4444' }}>{errors.images}</p>}
            </div>

            {/* Nom du produit */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colorMap.text.dark }}>Nom du produit *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Ex: iPhone 13 Pro 128GB"
                className="w-full px-4 py-3 rounded-lg border outline-none focus:border-orange-400 transition-colors"
                style={{ borderColor: errors.name ? '#ef4444' : colorMap.border.default }}
              />
              {errors.name && <p className="text-sm mt-1" style={{ color: '#ef4444' }}>{errors.name}</p>}
            </div>

            {/* Catégorie */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colorMap.text.dark }}>Catégorie *</label>
              <div className="relative">
                <Tag size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: colorMap.text.muted }} />
                <select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-lg border outline-none appearance-none focus:border-orange-400 transition-colors"
                  style={{ borderColor: errors.category ? '#ef4444' : colorMap.border.default }}
                >
                  <option value="">Choisir une catégorie</option>
                  {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              {errors.category && <p className="text-sm mt-1" style={{ color: '#ef4444' }}>{errors.category}</p>}
            </div>

            {/* Toggle Prix fixe / Prix variables */}
            <div>
              <label className="block text-sm font-medium mb-3" style={{ color: colorMap.text.dark }}>Type de tarification *</label>
              <div className="p-4 rounded-lg border-2" style={{ borderColor: colorMap.border.default }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <p className="font-semibold text-sm mb-1" style={{ color: colorMap.text.dark }}>
                      Prix fixe
                    </p>
                    <p className="text-xs" style={{ color: colorMap.text.muted }}>
                      {formData.isFixedPrice ? 'Un seul prix pour tous' : 'Prix selon la quantité'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleInputChange('isFixedPrice', !formData.isFixedPrice)}
                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none"
                    style={{
                      backgroundColor: formData.isFixedPrice ? colorMap.primary.base : colorMap.bg.disabled
                    }}
                  >
                    <span
                      className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300"
                      style={{
                        transform: formData.isFixedPrice ? 'translateX(26px)' : 'translateX(4px)'
                      }}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Prix fixe */}
            {formData.isFixedPrice ? (
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colorMap.text.dark }}>Prix (FCFA) *</label>
                <div className="relative">
                  <DollarSign size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: colorMap.text.muted }} />
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    placeholder="350000"
                    className="w-full pl-10 pr-4 py-3 rounded-lg border outline-none focus:border-orange-400 transition-colors"
                    style={{ borderColor: errors.price ? '#ef4444' : colorMap.border.default }}
                  />
                </div>
                {errors.price && <p className="text-sm mt-1" style={{ color: '#ef4444' }}>{errors.price}</p>}
              </div>
            ) : (
              /* Prix variables */
              <div>
                <label className="block text-sm font-medium mb-3" style={{ color: colorMap.text.dark }}>
                  Prix selon la quantité *
                </label>
                <div className="space-y-3">
                  {formData.variablePrices.map((vp, index) => (
                    <div key={index} className="flex gap-3 items-start">
                      <div className="flex-1">
                        <label className="block text-xs mb-1" style={{ color: colorMap.text.muted }}>Prix (FCFA)</label>
                        <input
                          type="number"
                          value={vp.price}
                          onChange={(e) => handleVariablePriceChange(index, 'price', e.target.value)}
                          placeholder="5000"
                          className="w-full px-3 py-2 rounded-lg border outline-none focus:border-orange-400 transition-colors"
                          style={{ borderColor: colorMap.border.default }}
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs mb-1" style={{ color: colorMap.text.muted }}>Quantité</label>
                        <input
                          type="number"
                          value={vp.quantity}
                          onChange={(e) => handleVariablePriceChange(index, 'quantity', e.target.value)}
                          placeholder="10"
                          className="w-full px-3 py-2 rounded-lg border outline-none focus:border-orange-400 transition-colors"
                          style={{ borderColor: colorMap.border.default }}
                        />
                      </div>
                      {formData.variablePrices.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeVariablePrice(index)}
                          className="mt-6 p-2 rounded-lg hover:bg-red-50 transition-colors"
                          style={{ color: '#ef4444' }}
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  ))}
                  
                  <button
                    type="button"
                    onClick={addVariablePrice}
                    className="w-full py-2.5 rounded-lg border-2 border-dashed flex items-center justify-center gap-2 font-medium hover:border-orange-400 transition-colors"
                    style={{ borderColor: colorMap.border.default, color: colorMap.secondary.base }}
                  >
                    <Plus size={18} />
                    <span>Ajouter un autre prix</span>
                  </button>
                </div>
                {errors.variablePrices && <p className="text-sm mt-2" style={{ color: '#ef4444' }}>{errors.variablePrices}</p>}
              </div>
            )}

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colorMap.text.dark }}>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Décrivez votre produit en détail : caractéristiques, état, raison de vente..."
                rows={4}
                className="w-full px-4 py-3 rounded-lg border outline-none resize-none focus:border-orange-400 transition-colors"
              />
              <p className="text-xs mt-1" style={{ color: colorMap.text.muted }}>
                Une description détaillée augmente vos chances de vente
              </p>
            </div>

            {/* Boutons d'action */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                style={{ backgroundColor: colorMap.bg.light, color: colorMap.text.dark }}
                disabled={uploading}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                style={{ backgroundColor: (uploading || isSubmitting) ? colorMap.secondary.light : colorMap.secondary.base, color: colorMap.bg.white, opacity: (uploading || isSubmitting) ? 0.7 : 1 }}
                disabled={uploading || isSubmitting}
              >
                {uploading ? <><Loader2 size={20} className="animate-spin" /><span>Upload...</span></> : isSubmitting ? <><Loader2 size={20} className="animate-spin" /><span>Publication...</span></> : <span>Publier le produit</span>}
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>
  );
}

export default function MyProductsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showAddProductForm, setShowAddProductForm] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, userInfo } = useAuth();
  const { fetchMyProducts } = useProducts();

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const data = await fetchMyProducts();
      setProducts(data || []);
    } catch (error) {
      console.error('Erreur:', error);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, [fetchMyProducts]);

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      return;
    }
    if (isAuthenticated) {
      loadProducts();
    }
    setIsLoading(false);
  }, [isAuthenticated, loadProducts, isLoading]);

  const formatCurrency = (amount) => new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';

  const formatPostedDate = (dateString) => {
    const now = new Date();
    const posted = new Date(dateString);
    const diffMs = now - posted;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        return `Il y a ${diffMinutes} min`;
      }
      return `Il y a ${diffHours}h`;
    }
    return diffDays === 1 ? 'Il y a 1 jour' : `Il y a ${diffDays} jours`;
  };

  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.category.toLowerCase().includes(searchQuery.toLowerCase())
  );
const stats = {
  totalProducts: products.length,
  totalViews: products.reduce((sum, product) => sum + Number(product.views || 0), 0),
  totalPurchases: products.reduce((sum, product) => sum + Number(product.purchases || 0), 0),
};


  const handleAddProduct = (newProduct) => {
    setProducts(prev => [newProduct, ...prev]);
    loadProducts();
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ? Cette action est irréversible.')) return;

    try {
      // Supprimer via Supabase (RLS garantit que seul le propriétaire peut supprimer)
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (error) throw error;
      setProducts(prev => prev.filter(p => p.id !== productId));
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression du produit');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colorMap.bg.light }}>
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-3" size={48} style={{ color: colorMap.secondary.base }} />
          <p style={{ color: colorMap.text.secondary }}>Chargement...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: colorMap.bg.light }}>
  <div className="max-w-md w-full p-8 rounded-2xl text-center shadow-lg" style={{ backgroundColor: colorMap.bg.white }}>
    {/* Icône accueillante */}
    <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: colorMap.secondary.light }}>
      <Store size={40} style={{ color: colorMap.secondary.base }} />
    </div>
    
    {/* Titre positif */}
    <h2 className="text-2xl font-bold mb-2" style={{ color: colorMap.text.primary }}>
      Bienvenue dans votre espace vendeur!
    </h2>
    
    {/* Sous-titre encourageant */}
    <p className="mb-6 text-sm" style={{ color: colorMap.text.secondary }}>
      Connectez-vous pour commencer à partager vos produits avec nos clients et développer votre activité.
    </p>
    
    {/* Avantages - présentés positivement */}
    <div className="rounded-lg p-4 mb-8 space-y-3">
      <div className="flex items-center gap-3 text-left">
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colorMap.secondary.light }}>
          <Upload size={16} style={{ color: colorMap.secondary.base }} />
        </div>
        <div>
          <p className="text-sm font-medium" style={{ color: colorMap.text.primary }}>
            Publiez vos produits
          </p>
          <p className="text-xs" style={{ color: colorMap.text.secondary }}>
            Créez des annonces attractives facilement
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-3 text-left">
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colorMap.secondary.light }}>
          <TrendingUp size={16} style={{ color: colorMap.secondary.base }} />
        </div>
        <div>
          <p className="text-sm font-medium" style={{ color: colorMap.text.primary }}>
            Suivez vos ventes
          </p>
          <p className="text-xs" style={{ color: colorMap.text.secondary }}>
            Analysez vos performances en temps réel
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-3 text-left">
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colorMap.secondary.light }}>
          <MessageSquare size={16} style={{ color: colorMap.secondary.base }} />
        </div>
        <div>
          <p className="text-sm font-medium" style={{ color: colorMap.text.primary }}>
            Échangez avec vos clients
          </p>
          <p className="text-xs" style={{ color: colorMap.text.secondary }}>
            Construisez des relations durables
          </p>
        </div>
      </div>
    </div>

    {/* CTA principal - encourageant */}
    <button
      onClick={() => navigate('/auth')}
      className="w-full py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity mb-3"
      style={{ backgroundColor: colorMap.secondary.base, color: colorMap.bg.white }}
    >
      <span>Se connecter</span>
      <ArrowRight size={18} />
    </button>
    
    {/* Lien secondaire - optionnel */}
    <p className="text-xs" style={{ color: colorMap.text.secondary }}>
      Vous n'avez pas encore de compte ?{' '}
      <span 
        onClick={() => navigate('/signup')}
        style={{ color: colorMap.secondary.base }}
        className="font-semibold cursor-pointer hover:underline"
      >
        Créer un compte gratuit
      </span>
    </p>
  </div>
</div>
    );
  }

  if (!userInfo?.shopName) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: colorMap.bg.light }}>
        <div className="max-w-md w-full p-8 rounded-2xl text-center shadow-lg" style={{ backgroundColor: colorMap.bg.white }}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: colorMap.secondary.light }}>
            <Store size={32} style={{ color: colorMap.secondary.base }} />
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: colorMap.text.primary }}>
            Configurez votre boutique
          </h2>
          <p className="mb-6" style={{ color: colorMap.text.secondary }}>
            Pour commencer à vendre vos produits, vous devez d'abord créer votre boutique en ligne. C'est rapide et gratuit !
          </p>

          <div className="rounded-lg p-4 mb-6" style={{ backgroundColor: colorMap.bg.light }}>
            <div className="flex items-start gap-3 text-left mb-4">
              <Sparkles size={20} style={{ color: colorMap.secondary.base }} className="flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-sm mb-1" style={{ color: colorMap.text.primary }}>
                  Ce que vous allez définir :
                </h3>
                <ul className="text-sm space-y-1" style={{ color: colorMap.text.secondary }}>
                  <li>• Nom de votre boutique</li>
                  <li>• Informations de contact</li>
                  <li>• Mode de livraison</li>
                  <li>• Méthodes de paiement</li>
                </ul>
              </div>
            </div>

            <div className="flex items-start gap-3 text-left">
              <TrendingUp size={20} style={{ color: colorMap.primary.base }} className="flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-sm mb-1" style={{ color: colorMap.text.primary }}>
                  Avantages :
                </h3>
                <ul className="text-sm space-y-1" style={{ color: colorMap.text.secondary }}>
                  <li>• Visibilité auprès de milliers d'étudiants</li>
                  <li>• Gestion simplifiée de vos ventes</li>
                  <li>• Statistiques en temps réel</li>
                </ul>
              </div>
            </div>
          </div>

          <button
            onClick={() => navigate('/complete-profile')}
            className="w-full py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
            style={{ backgroundColor: colorMap.secondary.base, color: colorMap.bg.white }}
          >
            <span>Créer ma boutique</span>
            <ArrowRight size={18} />
          </button>

          <p className="text-xs mt-4" style={{ color: colorMap.text.muted }}>
            ⏱️ Configuration en moins de 2 minutes
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: colorMap.bg.light }}>
      <header className="sticky top-0 z-10" style={{ backgroundColor: colorMap.bg.white, borderBottom: `1px solid ${colorMap.border.default}` }}>
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: colorMap.text.primary }}>Mes Ventes</h1>
              <div className="flex items-center gap-2 mt-1">
                <Store size={14} style={{ color: colorMap.text.muted }} />
                <p className="text-sm" style={{ color: colorMap.text.secondary }}>{userInfo.shopName}</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/notifications')}
              className="relative w-10 h-10 flex items-center justify-center rounded-full transition-colors hover:bg-gray-100"
              style={{ color: colorMap.text.primary }}
            >
              <Bell size={22} />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="text-center py-3 rounded-lg" style={{ backgroundColor: colorMap.bg.light }}>
              <Package size={16} style={{ color: colorMap.secondary.base }} className="mx-auto mb-1" />
              <div className="text-xl font-bold" style={{ color: colorMap.text.primary }}>{stats.totalProducts}</div>
              <div className="text-xs" style={{ color: colorMap.text.muted }}>Produits actifs</div>
            </div>
            <div className="text-center py-3 rounded-lg" style={{ backgroundColor: colorMap.bg.light }}>
              <Eye size={16} style={{ color: colorMap.text.muted }} className="mx-auto mb-1" />
              <div className="text-xl font-bold" style={{ color: colorMap.text.primary }}>{stats.totalViews}</div>
              <div className="text-xs" style={{ color: colorMap.text.muted }}>Vues totales</div>
            </div>
            <div className="text-center py-3 rounded-lg" style={{ backgroundColor: colorMap.primary.light }}>
              <ShoppingCart size={16} style={{ color: colorMap.primary.base }} className="mx-auto mb-1" />
              <div className="text-xl font-bold" style={{ color: colorMap.primary.base }}>{stats.totalPurchases}</div>
              <div className="text-xs" style={{ color: colorMap.text.muted }}>Achats</div>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2" size={18} style={{ color: colorMap.text.muted }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher par nom ou catégorie..."
              className="w-full pl-10 pr-3 py-2.5 rounded-lg border outline-none focus:border-orange-400 transition-colors"
              style={{ borderColor: colorMap.border.default, backgroundColor: colorMap.bg.light }}
            />
          </div>
        </div>
      </header>

      <div className="px-4 py-4 space-y-3">
        {loadingProducts ? (
          <div className="text-center py-12">
            <Loader2 className="animate-spin mx-auto mb-3" size={32} style={{ color: colorMap.secondary.base }} />
            <p style={{ color: colorMap.text.secondary }}>Chargement de vos produits...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            {searchQuery ? (
              <>
                <AlertCircle size={48} style={{ color: colorMap.text.muted }} className="mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2" style={{ color: colorMap.text.primary }}>
                  Aucun résultat trouvé
                </h3>
                <p className="text-sm mb-4" style={{ color: colorMap.text.secondary }}>
                  Aucun produit ne correspond à "{searchQuery}"
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="px-6 py-2.5 rounded-lg font-medium"
                  style={{ backgroundColor: colorMap.bg.light, color: colorMap.text.dark }}
                >
                  Réinitialiser la recherche
                </button>
              </>
            ) : (
              <>
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: colorMap.secondary.light }}>
                  <Package size={40} style={{ color: colorMap.secondary.base }} />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: colorMap.text.primary }}>
                  Votre boutique est vide
                </h3>
                <p className="text-sm mb-6 max-w-sm mx-auto" style={{ color: colorMap.text.secondary }}>
                  Commencez à vendre en ajoutant votre premier produit. C'est simple et rapide !
                </p>
                

                <button
                  onClick={() => setShowAddProductForm(true)}
                  className="px-6 py-3 rounded-xl font-semibold flex items-center gap-2 mx-auto hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: colorMap.secondary.base, color: colorMap.bg.white }}
                >
                  <Plus size={20} />
                  <span>Ajouter mon premier produit</span>
                </button>
              </>
            )}
          </div>
        ) : (
          filteredProducts.map((product) => (
            <div key={product.id} className="rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow" style={{ backgroundColor: colorMap.bg.white }}>
              <div className="flex gap-3 p-3">
                <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: colorMap.bg.light }}>
                  {(() => {
                    let parsedImages = [];
                    if (product.images) {
                      try {
                        parsedImages = typeof product.images === 'string' ? JSON.parse(product.images) : product.images;
                        if (typeof parsedImages === 'string') {
                          parsedImages = JSON.parse(parsedImages);
                        }
                      } catch (e) {
                        console.error('Erreur parsing images:', e);
                      }
                    }
                    const imageUrl = parsedImages?.[0]?.thumbnailUrl || parsedImages?.[0]?.url;
                    return imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package size={32} style={{ color: colorMap.text.muted }} />
                      </div>
                    );
                  })()}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold line-clamp-2 mb-2" style={{ color: colorMap.text.primary }}>{product.name}</h3>
                  <p className="text-lg font-bold mb-2" style={{ color: colorMap.secondary.base }}>{formatCurrency(product.price)}</p>
                  <div className="flex items-center gap-4 mb-2">
                    <div className="flex items-center gap-1 text-sm">
                      <Eye size={14} style={{ color: colorMap.text.muted }} />
                      <span style={{ color: colorMap.text.secondary }}>{product.views || 0} vues</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      <ShoppingCart size={14} style={{ color: colorMap.primary.base }} />
                      <span style={{ color: colorMap.text.secondary }}>{product.purchases || 0} achats</span>
                    </div>
                  </div>
                  <span className="text-xs" style={{ color: colorMap.text.muted }}>Publié {formatPostedDate(product.created_at)}</span>
                </div>
              </div>
              <div className="flex border-t" style={{ borderColor: colorMap.border.default }}>
                <button
                  className="flex-1 py-3 flex items-center justify-center gap-2 text-sm font-medium hover:bg-gray-50 transition-colors"
                  style={{ color: colorMap.secondary.base }}
                  onClick={() => alert('La modification de produit n\'est pas encore disponible.\nContactez le support au 0546641390 pour toute modification.')}
                >
                  <Edit size={16} />
                  <span>Modifier</span>
                </button>
                <div className="w-px" style={{ backgroundColor: colorMap.border.default }} />
                <button
                  className="flex-1 py-3 flex items-center justify-center gap-2 text-sm font-medium hover:bg-red-50 transition-colors"
                  style={{ color: '#ef4444' }}
                  onClick={() => handleDeleteProduct(product.id)}
                >
                  <Trash2 size={16} />
                  <span>Supprimer</span>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showAddProductForm && (
        <AddProductForm 
          onClose={() => setShowAddProductForm(false)}
          onSubmit={handleAddProduct}
          userInfo={userInfo}
        />
      )}
    </div>
  );
}